/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
'use client';

import clsx from 'clsx';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import tunnel from './tunnel.svg';
import { GradlephantNoHammer } from '@/components/Logos';
import { roboto_mono } from '@/app/fonts';
import { DocumentDuplicateIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { logoNavItems } from './nav-data';
import { useFathomEvent } from '@/lib/hooks/useFathomEvent';
import { FATHOM_EVENTS } from '@/lib/fathom-events';

export default function SubNav() {
  const [isCopied, setIsCopied] = useState<number>(-1);
  const pathname = usePathname();

  const trackGradleClick = useFathomEvent(FATHOM_EVENTS.scans['how-to'].gradle_icon.click);
  const trackMavenClick = useFathomEvent(FATHOM_EVENTS.scans['how-to'].maven_icon.click);
  const trackSbtClick = useFathomEvent(FATHOM_EVENTS.scans['how-to'].sbt_icon.click);
  const trackInstructionsCopy = useFathomEvent(FATHOM_EVENTS.scans['how-to'].instructions.copy);
  const trackInstructionsClick = useFathomEvent(FATHOM_EVENTS.scans['how-to'].instructions.click);

  const handleClickCopyIcon = (event: React.MouseEvent<HTMLDivElement>, idx: number, commandText: string) => {
    event.stopPropagation();
    navigator.clipboard.writeText(commandText);
    setIsCopied(idx);
    trackInstructionsCopy();
    setTimeout(() => {
      setIsCopied(-1);
    }, 2000);
  };

  const handleBuildToolClick = (buildType: string) => {
    if (buildType === 'Gradle') trackGradleClick();
    else if (buildType === 'Maven') trackMavenClick();
    else if (buildType === 'SBT') trackSbtClick();
  };

  const currentBuildToolConfig = logoNavItems.find(item => pathname.includes(item.path));
  if (!currentBuildToolConfig || !currentBuildToolConfig.bgImages) {
    return null;
  }

  return (
    <div className="py-section bg-gray-1" id="how-to">
      <div className="max-w-section bg relative flex flex-col items-center gap-6">
        <div className="relative flex h-[50rem] w-[25rem] items-center justify-center md:h-[42rem] md:w-[40rem] lg:h-[49rem] lg:w-full">
          <Image
            src={currentBuildToolConfig.bgImages.lg}
            alt="computer screen"
            className="absolute inset-0 hidden size-full lg:flex"
          />
          <Image
            src={currentBuildToolConfig.bgImages.md}
            alt="background design"
            className="absolute z-10 hidden size-full rounded-t-[3.5rem] object-cover md:flex lg:hidden"
          />
          <Image
            src={currentBuildToolConfig.bgImages.sm}
            alt="background design"
            className="absolute z-10 flex size-full rounded-t-[3.5rem] object-cover p-5 pb-[3.25rem] md:hidden"
          />
          <div className="absolute top-16 z-20 h-fit min-h-[35rem] w-[20rem] rounded-xl bg-white md:w-[37rem] lg:w-[60rem]">
            <div className="relative top-0 z-10 flex h-[33px] w-full items-center justify-center rounded-t-xl bg-light-background-100 px-5 md:h-14">
              <div className="absolute left-5 hidden gap-2 md:flex">
                <div className="size-3 rounded-full border border-[#d15c52] bg-[#ED6A5E]"></div>
                <div className="size-3 rounded-full border border-[#d4a445] bg-[#F4BD50]"></div>
                <div className="size-3 rounded-full border border-[#55ac4a] bg-[#61C454]"></div>
              </div>
              <div className="flex h-7 w-[18rem] items-center justify-center gap-2 rounded bg-light-background-100 md:w-[23rem] md:bg-light-background-200">
                <GradlephantNoHammer monochrome className="size-3 text-gray-3 opacity-50" />
                <p className="text-[12px] text-gray-2 md:text-[14px]">https://gradle.com/scans</p>
              </div>
            </div>
            <Image src={tunnel} alt="background grid" className="absolute inset-0 size-full rounded-xl" />
            <div className="mt-0 flex size-full flex-col items-center gap-5 px-4 pb-6 pt-4 md:-mt-14 md:px-8 md:pb-8 md:pt-20">
              <div className="z-10 flex flex-col gap-2 rounded-xl border border-gray-5 bg-white px-3 py-3 shadow-lg md:px-6">
                <p className="text-input-label text-center text-gray-4">Select your build system/package manager</p>
                <div className="relative flex justify-center gap-3">
                  {logoNavItems.map(item => (
                    <Link
                      key={item.label}
                      onClick={item.supported ? () => handleBuildToolClick(item.label) : e => e.preventDefault()}
                      href={item.path}
                      scroll={false}
                      className={clsx(
                        'relative flex size-14 items-center justify-center rounded-xl border px-2 py-2',
                        pathname.includes(item.path)
                          ? 'border-white text-white'
                          : 'bg-light-background-200 hover:text-gradle-blue',
                        item.supported ? 'border-gray-4' : 'hidden cursor-default border-light-background-200 md:flex',
                        pathname.includes(item.path) && item.activeBgGradient,
                      )}
                      prefetch={item.supported ? true : false}
                    >
                      {pathname.includes(item.path) ? (
                        <div className="flex items-center justify-center">{item.logoActive}</div>
                      ) : (
                        <div className="flex items-center justify-center">{item.logoInactive}</div>
                      )}
                      {pathname.includes(item.path) && (
                        <div
                          className={clsx(
                            'absolute bottom-0 -mb-[9px] h-1 w-[18px] rounded-full shadow-sm',
                            item.activeTabBG,
                          )}
                        ></div>
                      )}
                    </Link>
                  ))}
                  <div className="absolute bottom-0 right-0 hidden h-4 w-[calc(50%_-_6px)] items-center justify-center rounded-b-xl bg-gray-5 md:flex">
                    <p className="text-[10px] uppercase text-gray-3">Coming Soon</p>
                  </div>
                </div>
              </div>
              {/* Display the text content based on the selected tab */}
              <div className="z-10 flex flex-col gap-4 text-gray-1">
                <div key={currentBuildToolConfig.label} className="flex flex-col gap-3">
                  <div className="flex flex-col gap-2">
                    <h3 className={clsx('text-[17px] font-bold lg:text-[26px]', roboto_mono.className)}>
                      {currentBuildToolConfig.textContent.title}
                    </h3>
                    <p className="text-body text-gray-2">{currentBuildToolConfig.textContent.description}</p>
                  </div>
                  {currentBuildToolConfig.textContent.steps.map((step, index) => (
                    <div key={index} className="flex flex-col gap-2">
                      <div className="flex flex-col gap-1">
                        <p className="text-body-bold">{step.title}</p>
                        <p className="text-input-label text-gray-1">{step.description}</p>
                      </div>
                      {step.commandText && (
                        <div
                          className="flex items-center justify-between rounded-lg bg-light-background-200 p-3"
                          onClick={() => trackInstructionsClick()}
                        >
                          <p className={clsx('line-clamp-1 text-[13px] font-bold', roboto_mono.className)}>
                            {step.commandText}
                          </p>
                          <div
                            onClick={event => handleClickCopyIcon(event, index, step.commandText)}
                            className="cursor-pointer rounded-md p-1 hover:bg-gray-5"
                          >
                            {isCopied === index ? (
                              <CheckCircleIcon className="size-[18px] text-[#00C851]" />
                            ) : (
                              <DocumentDuplicateIcon className="size-[18px] min-w-[18px] text-gray-3" />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
